/**
 * Common validation rules for react-hook-form
 * Use these standardized rules across all forms for consistency
 */

/**
 * Email validation rule
 * Ensures the email contains @ symbol and basic email structure
 */
export const emailValidationRule = {
  pattern: {
    value: /^\S+@\S+$/i,
    message: 'Please enter a valid email address',
  },
};

/**
 * Strict email validation rule
 * Follows RFC 5322 standards more closely
 */
export const emailValidationRuleStrict = {
  pattern: {
    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: 'Please enter a valid email address',
  },
};

/**
 * Required email validation rule
 * Combines required and pattern validation
 */
export const requiredEmailValidation = {
  required: 'Email is required',
  ...emailValidationRule,
};

/**
 * Phone validation rule (optional but if provided must be valid)
 * Uses flexible phone format validation
 */
export const phoneValidationRule = {
  pattern: {
    value: /^(\+?\d{1,4})?[\s.-]?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}$/,
    message: 'Please enter a valid phone number',
  },
};

/**
 * Required phone validation rule
 */
export const requiredPhoneValidation = {
  required: 'Phone number is required',
  ...phoneValidationRule,
};

/**
 * Required field validation
 */
export const requiredField = (fieldName: string) => ({
  required: `${fieldName} is required`,
});

/**
 * Minimum length validation
 */
export const minLength = (length: number, fieldName: string = 'This field') => ({
  minLength: {
    value: length,
    message: `${fieldName} must be at least ${length} characters`,
  },
});

/**
 * Maximum length validation
 */
export const maxLength = (length: number, fieldName: string = 'This field') => ({
  maxLength: {
    value: length,
    message: `${fieldName} must not exceed ${length} characters`,
  },
});

/**
 * Array minimum length validation
 * Useful for checking if at least one item is selected
 */
export const minArrayLength = (length: number, fieldName: string = 'This field') => ({
  validate: (value: unknown) => {
    if (Array.isArray(value) && value.length >= length) {
      return true;
    }
    return `Please select at least ${length} ${fieldName.toLowerCase()}${length > 1 ? 's' : ''}`;
  },
});

/**
 * Date of birth validation - prevents future dates
 * Validates that the date is not in the future
 */
export const dateOfBirthValidation = {
  validate: (value: unknown) => {
    if (!value) return true; // Optional field

    const selectedDate = new Date(value as string);
    const today = new Date();

    // Set time to midnight for accurate date comparison
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return 'Date of birth cannot be in the future';
    }

    return true;
  },
};

/**
 * Past date validation - prevents future dates for any date field
 * Generic validation for dates that should not be in the future
 */
export const pastDateValidation = {
  validate: (value: unknown) => {
    if (!value) return true; // Optional field

    const selectedDate = new Date(value as string);
    const today = new Date();

    // Set time to midnight for accurate date comparison
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return 'Date cannot be in the future';
    }

    return true;
  },
};

/**
 * Contacts list validation
 * Validates that at least one contact exists and all contacts have required fields
 */
export const contactsValidation = {
  validate: (value: unknown) => {
    if (!value || !Array.isArray(value)) {
      return true; // Optional - no contacts is valid
    }

    // If contacts exist, validate each one
    for (let i = 0; i < value.length; i++) {
      const contact = value[i];

      if (!contact.firstName || contact.firstName.trim() === '') {
        return `Contact ${i + 1}: First name is required`;
      }

      if (!contact.lastName || contact.lastName.trim() === '') {
        return `Contact ${i + 1}: Last name is required`;
      }

      // Validate email format if provided
      if (contact.email && contact.email.trim() !== '') {
        const emailPattern = /^\S+@\S+$/i;
        if (!emailPattern.test(contact.email)) {
          return `Contact ${i + 1}: Invalid email format`;
        }
      }

      const phonePattern = /^(\+?\d{1,4})?[\s.-]?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}$/;
      const hasMobile = contact.phoneMobile && contact.phoneMobile.trim() !== '';

      if (hasMobile && !phonePattern.test(contact.phoneMobile)) {
        return `Contact ${i + 1}: Invalid mobile phone number format`;
      }
    }

    return true;
  },
};

/**
 * Required contacts validation
 * Validates that at least one contact exists
 */
export const requiredContactsValidation = {
  validate: (value: unknown) => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return 'At least one contact is required';
    }

    // Run the standard contacts validation
    return contactsValidation.validate(value);
  },
};
